import { createContext, useState, useContext, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { StringUtils } from "../../../../../core/utils/string_utils";
import { userRepository, trackingService } from "../../../di/di";

const ResetPasswordContext = createContext();

/**
 * Provides a context for managing the reset password functionality.
 * 
 * This context includes the state and logic necessary for handling
 * the process of resetting a user's password.
 * 
 * State:
 * - init: A boolean indicating if the component has finished initializing.
 * - name: The name of the user who is resetting their password.
 * - password: The new password input by the user.
 * - passwordErrorMessage: An error message if the new password is invalid.
 * - confirmPassword: The confirm password input by the user.
 * - confirmPasswordErrorMessage: An error message if the confirm password does not match the new password.
 * - error: An error message if the reset password request fails.
 * - isProcessing: A boolean indicating if the reset password request is in progress.
 * - isSuccess: A boolean indicating if the reset password was successful.
 * 
 * Methods:
 * - handlePasswordChange: An event handler that updates the password state and validation.
 * - handleConfirmPasswordChange: An event handler that updates the confirm password state and validation.
 * - handleResetPasswordClick: An async function that attempts to reset the user's password.
 * 
 * @param {Object} children - The children components that will have access to this context.
 */
export const ResetPasswordContextProvider = ({ children }) => {
    const params = useParams();
    const {token} = params;
    const [init, setInit] = useState(false);
    const [name, setName] = useState('');
    const [password, setPassword] = useState("");
    const [passwordErrorMessage, setPasswordErrorMessage] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [confirmPasswordErrorMessage, setConfirmPasswordErrorMessage] = useState("");
    const [error, setError] = useState("");
    const [isProcessing, setIsProcessing] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const isButtonDisabled = !!passwordErrorMessage || !!confirmPasswordErrorMessage || !password || !confirmPassword || isProcessing;

/**
 * Handles the password change event.
 * Clears existing password error message, validates the new password,
 * and updates the password state. If the password is invalid, sets the
 * password error message. Additionally, checks if the new password matches
 * the confirmation password and sets the confirm password error message
 * if they do not match.
 * 
 * @param {React.ChangeEvent<HTMLInputElement>} e - The event object containing the new password value.
 */

    const handlePasswordChange = (e) => {
        setPasswordErrorMessage("");

        const value = e.target.value;

        const validation = StringUtils.isPasswordValid(value);
        
        if(!validation.isValid){
            setPasswordErrorMessage(validation.message);
        }

        if(value !== confirmPassword && confirmPassword){
            setConfirmPasswordErrorMessage("Passwords do not match");
        }

        setPassword(value);
    }

    /**
     * Handles the confirm password change event.
     * Clears existing confirm password error message, validates
     * if the confirm password matches the password, and updates
     * the confirm password state. If the confirm password does
     * not match the password, sets the confirm password error
     * message.
     * 
     * @param {React.ChangeEvent<HTMLInputElement>} e - The event object containing the new confirm password value.
     */
    const handleConfirmPasswordChange = (e) => {
        setConfirmPasswordErrorMessage("");

        const value = e.target.value;

        if(value !== password){
            setConfirmPasswordErrorMessage("Passwords do not match");
        }

        setConfirmPassword(value);
    }

    /**
     * Handles the reset password button click event.
     * Submits the new password and the reset password token to the server.
     * If the request is successful, sets the success state to true.
     * If the request fails, sets the error message.
     * Finally, sets the isProcessing state to false.
     */
    const handleResetPasswordClick = async () => {
        try{
            trackingService.trackEvent("reset_password_initiated", { timestamp: new Date().toISOString() });
            setIsProcessing(true);
            const apiResponse = await userRepository.resetPassword(token, password);

            if(!apiResponse.success){
                setError(apiResponse.message);
                trackingService.trackEvent("reset_password_failed", { failure_reason: apiResponse.message, timestamp: new Date().toISOString() });
                return;
            }
            trackingService.trackEvent("reset_password_success", { timestamp: new Date().toISOString() });
            setIsSuccess(true);
        }
        catch(e){
            if(e.response?.data?.message) setError(e.response.data.message);
            trackingService.trackEvent("reset_password_failed", { failure_reason: e.message, timestamp: new Date().toISOString() });
        }
        finally{
            setIsProcessing(false);
        }
    }

    const checkToken = useCallback(async () => {
        try{
            const apiResponse = await userRepository.validateRecoveryLink(token);

            if(!apiResponse.success){
                setError(apiResponse.message);
                return;
            }

            setName(apiResponse.data.name);
        }
        catch(e){
            if(e.response?.data?.message) setError(e.response.data.message);
        }
        finally{
            setInit(true);
        }
    }, [token]);

    useEffect(() => {
        checkToken();
    }, [checkToken]);

    const values = {
        password,
        passwordErrorMessage,
        confirmPassword,
        confirmPasswordErrorMessage,
        isProcessing,
        isSuccess,
        isButtonDisabled,
        error,
        name,
        init,

        handlePasswordChange,
        handleConfirmPasswordChange,
        handleResetPasswordClick,
    }

    return (
        <ResetPasswordContext.Provider value={values}>
            {children}
        </ResetPasswordContext.Provider>
    )
}

export const useResetPasswordContext = () => useContext(ResetPasswordContext);